import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { getYoutubeVideoId, isYoutubeShort } from '../../utils/youtube';
import { stripWrappingQuotes, OPEN_QUOTE, CLOSE_QUOTE } from '../../utils/text';
import { WIDGET_LAYOUTS, renderWidgetLayout } from '../../components/WidgetLayouts';
import { useTestimonials } from '../../context/TestimonialContext';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiMonitor, FiSmartphone, FiStar, FiHeart, FiMessageSquare, FiCode, FiCheckCircle, FiSearch, FiLayout, FiType, FiAlertTriangle, FiYoutube } from 'react-icons/fi';

// ----------------------------------------------------------------------
// GOOGLE FONTS HOOK
// ----------------------------------------------------------------------
const useGoogleFont = (fontFamily) => {
  useEffect(() => {
    if (!fontFamily) return;
    const fontName = fontFamily.replace(/ /g, '+');
    const url = `https://fonts.googleapis.com/css2?family=${fontName}:wght@300;400;500;600;700;800&display=swap`;
    
    const existingLink = document.querySelector(`link[href="${url}"]`);
    if (existingLink) return;
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
    
    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [fontFamily]);
};

// ----------------------------------------------------------------------
// CUSTOM ICONS
// ----------------------------------------------------------------------
const DiamondIcon = ({ className, style }) => (
  <svg viewBox="0 0 122.88 96.04" fill="currentColor" className={className || "w-4 h-4"} style={style}>
    <path fillRule="evenodd" clipRule="evenodd" d="M59.07,96.03L1.15,29.77h38.01L59.07,96.03L59.07,96.03L59.07,96.03z M61.18,0.11L45.89,25.24H77.6L61.18,0.11 L61.18,0.11z M82.18,25.07L65.17,0h31.45L82.18,25.07L82.18,25.07z M41.65,25.31L56.55,0H24.61L41.65,25.31L41.65,25.31z M101.71,2.98L86.67,25.24h36.21L101.71,2.98L101.71,2.98z M22.26,2.98l15.81,22.26H0L22.26,2.98L22.26,2.98L22.26,2.98z M44.73,29.57h33.63L62.04,95.04L44.73,29.57L44.73,29.57L44.73,29.57z M64.91,96.04l57.23-66.27l-38.01,0L64.91,96.04L64.91,96.04 L64.91,96.04z" />
  </svg>
);

const BananaIcon = ({ className, style }) => (
  <svg viewBox="0 0 1024 1024" fill="currentColor" className={className || "w-4 h-4"} style={style}>
    <path d="M609.3 909.7c-126.3 0-252.8-54-346.9-148.1C78.5 577.7 64.1 293.1 230.1 127l1.2-1.2c7.6-7.6 17-11.6 27.2-11.6 11.4 0 22.8 5.2 30.5 13.8 5.4 6 11.4 16.3 9.7 31.4-16.2 143.8 41.8 297.4 155.1 410.7C554 670.5 686.2 728 816.5 728c16.1 0 32.3-0.9 48.1-2.7 1.7-0.2 3.4-0.3 5.1-0.3 19.6 0 32 12.9 37 25 6.3 15 3.1 31-8.5 42.6l-1.2 1.2c-74.7 74.8-176.9 115.9-287.7 115.9z" />
    <path d="M258.5 130.5c6.8 0 13.7 3.1 18.4 8.4 4.6 5.1 6.5 11.4 5.6 18.7-16.8 148.7 42.9 307.2 159.8 424.1C545.6 685 682 744.3 816.5 744.3c16.7 0 33.5-0.9 49.9-2.8 1.1-0.1 2.2-0.2 3.3-0.2 11.6 0 19 7.8 22 15 2.2 5.2 4.4 15.4-5 24.9l-1.1 1.1C813.8 854 715.8 893.5 609.3 893.5c-59.7 0-118.9-12.1-176-35.9-59.2-24.8-112.9-60.9-159.4-107.5-86.4-86.4-137.2-198-142.9-314.1-5.6-114.9 33.6-220.6 110.5-297.5l1.1-1.1c4.7-4.6 10-6.9 15.9-6.9m-0.1-32.5c-13.5 0-27.2 5-38.6 16.3l-1.2 1.2C45.9 288.2 60.4 582.6 250.9 773.1 352.3 874.5 483.1 926 609.3 926c111 0 218.4-39.8 299.2-120.6l1.2-1.2c35.7-36 8.4-95.4-40.1-95.4-2.3 0-4.6 0.1-6.9 0.4-15.2 1.7-30.7 2.6-46.2 2.6-121.2 0-250.1-52-351.2-153-114-114-165.6-263.5-150.5-397.4C319 124.5 289.5 98 258.4 98z" />
    <path d="M729.2 811C444.6 811 213 579.4 213 294.8c0-9 7.3-16.3 16.3-16.3s16.3 7.3 16.3 16.3c0 266.7 217 483.8 483.8 483.8 9 0 16.3 7.3 16.3 16.3-0.2 8.9-7.5 16.1-16.5 16.1z" />
    <path d="M191.1 172.6c11.8-16.2 24.9-31.4 39-45.6l1.2-1.2c7.6-7.6 17.1-11.6 27.2-11.6 11.4 0 22.8 5.2 30.5 13.8 5.4 6 11.4 16.3 9.7 31.4-1.5 13.5-2.4 27.3-2.6 41.2l-105-28z" />
    <path d="M258.5 130.5c6.8 0 13.7 3.1 18.4 8.4 4.6 5.1 6.5 11.4 5.6 18.7-0.8 7.3-1.5 14.6-1.9 22.1L219 163.2c7.2-8.6 14.7-16.8 22.6-24.7l1.1-1.1c4.6-4.6 9.9-6.9 15.8-6.9m0-32.5c-13.5 0-27.2 5-38.6 16.3l-1.2 1.2c-20.7 20.7-38.6 43.1-53.9 66.8L312.5 222c-0.7-20.4 0-40.7 2.3-60.6C319 124.5 289.5 98 258.5 98z" />
  </svg>
);

// ----------------------------------------------------------------------
// CUSTOM UI COMPONENTS
// ----------------------------------------------------------------------
const SectionCard = ({ title, children, className = '' }) => (
  <div className={`relative p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-xl border border-orange-500/10 shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:border-orange-500/30 hover:shadow-[0_8px_30px_rgba(234,88,12,0.05)] transition-all duration-300 ${className}`}>
    <h3 className="text-xs font-bold tracking-widest text-orange-500 uppercase mb-5">{title}</h3>
    {children}
  </div>
);

const Toggle = ({ label, enabled, onChange }) => (
  <div className="flex items-center justify-between cursor-pointer group py-1" onClick={() => onChange(!enabled)}>
    <span className="text-sm font-medium text-zinc-400 group-hover:text-amber-100 transition-colors duration-300">{label}</span>
    <div className={`w-10 h-5 rounded-full relative transition-colors duration-300 shadow-inner ${enabled ? 'bg-gradient-to-r from-orange-600 to-yellow-500' : 'bg-zinc-800 border border-white/5'}`}>
      <motion.div 
        layout 
        className="w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] shadow-sm"
        animate={{ left: enabled ? '22px' : '4px' }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      />
    </div>
  </div>
);

const Checkbox = ({ label, checked, onChange, labelClassName = '' }) => (
  <label className="flex items-center gap-3 cursor-pointer group py-1">
    <div className={`w-5 h-5 rounded flex items-center justify-center transition-all duration-300 border flex-shrink-0 ${checked ? 'bg-orange-500 border-orange-400 shadow-[0_0_10px_rgba(234,88,12,0.4)]' : 'bg-zinc-900/50 border-orange-500/20 group-hover:border-orange-500/50'}`}>
      {checked && <SafeIcon icon={FiCheckCircle} className="text-white text-xs" />}
    </div>
    <input 
      type="checkbox" 
      className="hidden" 
      checked={checked} 
      onChange={(e) => onChange(e.target.checked)} 
    />
    <span className={`text-sm font-medium text-zinc-400 group-hover:text-amber-100 transition-colors duration-300 ${labelClassName}`}>{label}</span>
  </label>
);

const CustomSelect = ({ value, options, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const selectedOption = options.find(o => o.value === value) || options[0] || { label: 'Selecting...', value: '' };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      <button 
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-[#0a0500]/60 backdrop-blur-md border border-orange-500/20 rounded-xl px-4 py-3 text-sm font-medium text-amber-50 flex items-center justify-between hover:border-orange-500/50 hover:shadow-[0_0_20px_rgba(234,88,12,0.15)] transition-all outline-none"
      >
        <div className="flex items-center gap-3">
          {selectedOption.icon && <span className="flex items-center justify-center text-orange-400">{selectedOption.icon}</span>}
          <span className="truncate">{selectedOption.label}</span>
        </div>
        <div className={`text-zinc-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
      </button>

      <AnimatePresence>
        {isOpen && options.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-zinc-950/95 backdrop-blur-xl border border-orange-500/20 rounded-xl shadow-[0_15px_40px_rgba(0,0,0,0.5)] overflow-hidden max-h-64 overflow-y-auto"
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all duration-300 hover:bg-orange-500/10 ${value === opt.value ? 'text-amber-100 bg-orange-500/10 border-l-2 border-orange-500' : 'text-zinc-400 border-l-2 border-transparent'}`}
              >
                {opt.icon && <span className={`flex items-center justify-center text-lg ${value === opt.value ? 'text-orange-400' : 'text-zinc-500'}`}>{opt.icon}</span>}
                <span className="truncate" style={{ fontFamily: opt.value }}>{opt.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ----------------------------------------------------------------------
// MAIN PAGE COMPONENT
// ----------------------------------------------------------------------
const WidgetBuilder = () => {
  const { user } = useAuth();
  const { testimonials } = useTestimonials();
  
  const [widgets, setWidgets] = useState([]);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState(true);
  const [activeWidget, setActiveWidget] = useState(null);
  
  const [previewMode, setPreviewMode] = useState('desktop');
  const [embedCopied, setEmbedCopied] = useState(false);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  // Configuration State
  const [config, setConfig] = useState({
    layout: 'masonry',
    bgColor: '#0a0500',
    accentColor: '#ea580c',
    fontFamily: 'Plus Jakarta Sans',
    ratingStyle: 'stars',
    videoOnly: false,
    textOnly: false,
    keyword: '',
    manualSelection: false,
    disableAutoResize: false
  });
  
  const [manualSelectionIds, setManualSelectionIds] = useState([]);

  // Inject Google Font
  useGoogleFont(config.fontFamily);

  // Fetch users widgets on mount
  useEffect(() => {
    if (!user?.id) return;
    
    const fetchWidgets = async () => {
      setIsLoadingWidgets(true);
      const { data, error } = await supabase
        .from('widgets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (data && data.length > 0) {
        setWidgets(data);
        if (!activeWidget) setActiveWidget(data[0].id);
      } else {
        // Auto-create a default widget for new users
        handleCreateWidget('My First Widget');
      }
      setIsLoadingWidgets(false);
    };
    
    fetchWidgets();
  }, [user?.id]);

  // Apply config when activeWidget changes
  useEffect(() => {
    if (activeWidget && widgets.length > 0) {
      const w = widgets.find(x => x.id === activeWidget);
      if (w) applyWidgetConfig(w);
    }
  }, [activeWidget]);

  const applyWidgetConfig = (w) => {
    setConfig({
      layout: w.layout || 'masonry',
      bgColor: w.theme_config?.bgColor || '#0a0500',
      accentColor: w.theme_config?.accentColor || '#ea580c',
      fontFamily: w.theme_config?.fontFamily || 'Plus Jakarta Sans',
      ratingStyle: w.theme_config?.ratingStyle || 'stars',
      videoOnly: w.theme_config?.videoOnly || false,
      textOnly: w.theme_config?.textOnly || false,
      keyword: w.theme_config?.keyword || '',
      manualSelection: w.theme_config?.manualSelection || false,
      disableAutoResize: w.theme_config?.disableAutoResize || false
    });
    setManualSelectionIds(w.manual_selection_ids || []);
  };

  const handleCreateWidget = async (defaultName) => {
    const name = typeof defaultName === 'string' ? defaultName : prompt('Enter widget name:', `Widget ${widgets.length + 1}`);
    if (!name) return;

    const newWidget = {
      user_id: user.id,
      name,
      layout: 'masonry',
      theme_config: {
        bgColor: '#0a0500',
        accentColor: '#ea580c',
        fontFamily: 'Plus Jakarta Sans',
        ratingStyle: 'stars',
        videoOnly: false,
        textOnly: false,
        keyword: '',
        manualSelection: false,
        disableAutoResize: false
      },
      manual_selection_ids: []
    };

    try {
      const { data, error } = await supabase.from('widgets').insert([newWidget]).select().single();
      if (error) throw error;
      if (data) {
        setWidgets([data, ...widgets]);
        setActiveWidget(data.id);
      }
    } catch (err) {
      console.error('Error creating widget:', err);
      alert(err.message || 'Failed to create widget. Please try again.');
    }
  };

  const handleSave = async () => {
    if (!activeWidget) return;
    setIsSaving(true);
    setSaveError('');
    setSaveMessage('');
    
    try {
      const updatedThemeConfig = {
        bgColor: config.bgColor,
        accentColor: config.accentColor,
        fontFamily: config.fontFamily,
        ratingStyle: config.ratingStyle,
        videoOnly: config.videoOnly,
        textOnly: config.textOnly,
        keyword: config.keyword,
        manualSelection: config.manualSelection,
        disableAutoResize: config.disableAutoResize
      };

      const { error } = await supabase.from('widgets').update({
        layout: config.layout,
        theme_config: updatedThemeConfig,
        manual_selection_ids: manualSelectionIds
      }).eq('id', activeWidget);

      if (error) throw error;
      
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
      
      setWidgets(prev => prev.map(w => 
        w.id === activeWidget 
          ? { ...w, layout: config.layout, theme_config: updatedThemeConfig, manual_selection_ids: manualSelectionIds } 
          : w
      ));
    } catch (err) {
      console.error('Error saving widget:', err);
      setSaveError('Failed to save');
      setTimeout(() => setSaveError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyEmbed = () => {
    if (!activeWidget) return;
    const embedCode = `<iframe src="https://moment.reviews/embed/${activeWidget}" width="100%" height="100%" frameborder="0" scrolling="no" style="border: none;overflow: hidden;min-height: 500px;"></iframe>\n<script>\n  window.addEventListener('message', function(e) {\n    if (e.data && e.data.type === 'WIDGET_RESIZE') {\n      const iframes = document.querySelectorAll('iframe[src*="moment.reviews"]');\n      iframes.forEach(iframe => iframe.style.height = e.data.height + 'px');\n    }\n  });\n</script>`;
    navigator.clipboard.writeText(embedCode);
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  // Dropdown Options
  const savedWidgetOptions = widgets.length > 0 
    ? widgets.map(w => ({ value: w.id, label: w.name, icon: <SafeIcon icon={FiLayout} /> }))
    : [{ value: '', label: 'No widgets available', icon: <SafeIcon icon={FiLayout} /> }];

  const layoutOptions = WIDGET_LAYOUTS.map(l => ({
    ...l,
    icon: <SafeIcon icon={FiLayout} />
  }));

  const bgPresets = [
    'transparent', '#0a0500', '#000000', '#09090b', '#020617', '#171717', '#111827', '#0f172a', '#1e1b4b', '#1a0b2e', '#2e1065', '#1c1917', '#18181b'
  ];
  
  const accentPresets = [
    '#ea580c', '#eab308', '#10b981', '#3b82f6', '#8b5cf6', '#f43f5e', '#06b6d4', '#f97316'
  ];

  const fontFamilies = [
    'Plus Jakarta Sans', 'Inter', 'Roboto', 'Playfair Display', 'Outfit', 'Space Grotesk'
  ];
  
  const typographyOptions = fontFamilies.map(font => ({
    value: font, label: font, icon: <SafeIcon icon={FiType} />
  }));

  const ratingStyleOptions = [
    { value: 'stars', label: 'Classic Stars', icon: <SafeIcon icon={FiStar} /> },
    { value: 'hearts', label: 'Hearts', icon: <SafeIcon icon={FiHeart} /> },
    { value: 'diamonds', label: 'Diamonds', icon: <DiamondIcon /> },
    { value: 'bananas', label: 'Bananas', icon: <BananaIcon /> },
  ];

  // Filter Logic for Preview
  let displayTestimonials = testimonials;
  
  if (config.manualSelection) {
    displayTestimonials = displayTestimonials.filter(t => manualSelectionIds.includes(t.id));
  }
  
  if (config.videoOnly) displayTestimonials = displayTestimonials.filter(t => t.source === 'YouTube');
  if (config.textOnly) displayTestimonials = displayTestimonials.filter(t => t.source !== 'YouTube');
  
  if (config.keyword) {
    const kw = config.keyword.toLowerCase();
    displayTestimonials = displayTestimonials.filter(t => 
      (t.content && t.content.toLowerCase().includes(kw)) || 
      (t.authorName && t.authorName.toLowerCase().includes(kw))
    );
  }

  const renderRating = (rating) => {
    const r = rating || 5;
    if (config.ratingStyle === 'bananas') {
      return [...Array(r)].map((_, idx) => (
        <BananaIcon key={idx} className="w-3.5 h-3.5" style={{ color: config.accentColor, filter: `drop-shadow(0 0 4px ${config.accentColor}40)` }} />
      ));
    }
    if (config.ratingStyle === 'diamonds') {
      return [...Array(r)].map((_, idx) => (
        <DiamondIcon key={idx} className="w-3.5 h-3.5" style={{ color: config.accentColor, filter: `drop-shadow(0 0 4px ${config.accentColor}40)` }} />
      ));
    }
    if (config.ratingStyle === 'hearts') {
      return [...Array(r)].map((_, idx) => (
        <SafeIcon key={idx} icon={FiHeart} style={{ color: config.accentColor, fill: config.accentColor }} className="text-sm" />
      ));
    }
    return [...Array(r)].map((_, idx) => (
      <SafeIcon key={idx} icon={FiStar} style={{ color: config.accentColor, fill: config.accentColor }} className="text-sm" />
    ));
  };

  const SingleReviewCard = ({ t }) => {
    const isVideo = t.source?.toLowerCase() === 'youtube' || !!t.youtubeLink;
    const videoId = isVideo ? getYoutubeVideoId(t.youtubeLink) : null;
    const isShort = isVideo ? isYoutubeShort(t.youtubeLink) : false;
    const isPureVideo = isVideo && (!t.content || t.content.trim() === '');

    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="break-inside-avoid rounded-2xl backdrop-blur-md shadow-lg border transition-all duration-300 hover:-translate-y-1 h-full flex flex-col overflow-hidden"
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderColor: 'rgba(255, 255, 255, 0.08)'
        }}
      >
        {/* Media Top */}
        {videoId && (
          <div className="w-full flex-shrink-0 relative z-0 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
            <LiteYouTubeEmbed
              id={videoId}
              title="YouTube Testimonial"
              wrapperClass={`yt-lite ${isShort ? 'yt-short' : ''}`}
              noCookie={true}
              params="rel=0&modestbranding=1"
            />
            <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-md rounded-md text-xs font-medium text-white/80 border border-white/10 flex items-center gap-1.5 pointer-events-none z-10">
              <SafeIcon icon={FiYoutube} style={{ color: config.accentColor }} /> YouTube
            </div>
          </div>
        )}

        {/* Text Content */}
        {!isPureVideo && (
          <div className="p-5 sm:p-6 pb-2 flex-grow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-1 items-center">
                {renderRating(t.rating)}
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-6 break-words" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {OPEN_QUOTE}{stripWrappingQuotes(t.content)}{CLOSE_QUOTE}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 mt-auto p-5 sm:p-6 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.05)', backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
          {/* Concurrent Avatars */}
          <div className="flex items-center -space-x-2 flex-shrink-0">
            {t.clientAvatarUrl ? (
              <div className="w-10 h-10 rounded-full border-2 relative z-20 overflow-hidden shadow-sm" style={{ borderColor: config.bgColor === 'transparent' ? '#18181b' : config.bgColor, backgroundColor: '#27272a' }}>
                <img src={t.clientAvatarUrl} alt={t.authorName} className="w-full h-full object-cover" />
              </div>
            ) : (!t.companyLogo && (
              <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center relative z-20 shadow-sm" style={{ borderColor: config.bgColor === 'transparent' ? '#18181b' : config.bgColor, background: `linear-gradient(135deg, ${config.accentColor}40, rgba(0,0,0,0.5))` }}>
                <span className="text-white font-semibold">{t.authorName?.charAt(0) || 'A'}</span>
              </div>
            ))}
            {t.companyLogo && (
              <div className="w-10 h-10 rounded-full border-2 relative z-10 overflow-hidden shadow-sm bg-white" style={{ borderColor: config.bgColor === 'transparent' ? '#18181b' : config.bgColor }}>
                <img src={t.companyLogo} alt={t.companyName} className="w-full h-full object-contain p-0.5" />
              </div>
            )}
          </div>
          
          <div className="overflow-hidden flex-1 pl-1">
            <div className="text-white font-semibold text-sm truncate">
              {t.authorName || t.companyName || 'Anonymous'}
            </div>
            <div className="text-white opacity-60 text-[11px] truncate flex items-center gap-1.5">
              {t.authorRole && <span>{t.authorRole}</span>}
              {t.authorRole && t.companyName && <span>•</span>}
              {t.companyName && <span className="font-medium opacity-80">{t.companyName}</span>}
            </div>
          </div>
          
          {t.source && (
            <div className="px-2 py-1 rounded-md text-[10px] font-medium text-white/50 border border-white/10 flex items-center gap-1.5 flex-shrink-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
              <SafeIcon icon={t.source.toLowerCase() === 'youtube' ? FiYoutube : FiMessageSquare} className="text-white/40" /> {t.source}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // The mobile preview narrows the CONTAINER, but Tailwind's sm:/md:
  // breakpoints respond to the viewport — so the shared renderer suppresses
  // them when isMobile is set, or the phone frame shows a squeezed desktop grid.
  const renderLayout = () => renderWidgetLayout({
    layout: config.layout,
    items: displayTestimonials,
    Card: (t) => <SingleReviewCard t={t} />,
    isMobile: previewMode === 'mobile'
  });

  return (
    <div className="max-w-[1400px] mx-auto pb-12">
      {/* Top Section: Saved Widgets Dropdown & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
          <div className="w-full sm:w-80 relative z-[80]">
            <CustomSelect
              value={activeWidget || ''}
              onChange={setActiveWidget}
              options={savedWidgetOptions}
            />
          </div>
        </div>

        {/* Global Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {(() => {
            // Server-enforced too (widgets_enforce_limit trigger); this is the
            // polite version of the same rule.
            const atFreeLimit = user?.tier === 'free' && widgets.length >= 1;
            return (
              <Button
                variant="secondary"
                onClick={atFreeLimit ? undefined : handleCreateWidget}
                disabled={atFreeLimit}
                title={atFreeLimit ? 'Free plan includes 1 widget — upgrade to Pro for unlimited widgets' : undefined}
                className={`!py-3 !px-5 text-sm flex-shrink-0 ${atFreeLimit ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <SafeIcon icon={FiPlus} /> New Widget
              </Button>
            );
          })()}
          {activeWidget && (
            <>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={isSaving}
                className={`!py-3 !px-5 text-sm transition-all duration-300 ${saveError ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : ''}`}
              >
                {isSaving ? (
                  'Saving...'
                ) : saveError ? (
                  <><SafeIcon icon={FiAlertTriangle} /> {saveError}</>
                ) : saveMessage ? (
                  <><SafeIcon icon={FiCheckCircle} className="text-green-400" /> Saved!</>
                ) : (
                  'Save Settings'
                )}
              </Button>
              <Button variant="primary" onClick={handleCopyEmbed} className="!py-3 !px-5 text-sm">
                <SafeIcon icon={embedCopied ? FiCheckCircle : FiCode} className={embedCopied ? "text-green-400" : ""} /> {embedCopied ? 'Copied!' : 'Embed Code'}
              </Button>
            </>
          )}
        </div>
      </div>

      {!isLoadingWidgets && widgets.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Panel: Configuration Controls */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <SectionCard title="Layout" className="z-[70]">
              <CustomSelect 
                value={config.layout} 
                onChange={(val) => setConfig({...config, layout: val})} 
                options={layoutOptions} 
              />
            </SectionCard>

            <SectionCard title="Theme settings" className="z-[60]">
              <div className="space-y-6">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-3 block">Premium Backgrounds</label>
                  <div className="flex flex-wrap gap-2.5">
                    {bgPresets.map(color => {
                      const isTransparent = color === 'transparent';
                      // Checkerboard fill signals "transparent" the way image editors do.
                      const checkerStyle = {
                        backgroundImage:
                          'linear-gradient(45deg, #bbb 25%, transparent 25%), linear-gradient(-45deg, #bbb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #bbb 75%), linear-gradient(-45deg, transparent 75%, #bbb 75%)',
                        backgroundSize: '8px 8px',
                        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                        backgroundColor: '#fff'
                      };
                      return (
                        <button
                          key={color}
                          onClick={() => setConfig({...config, bgColor: color})}
                          style={isTransparent ? checkerStyle : { backgroundColor: color }}
                          className={`w-8 h-8 rounded-full border transition-all duration-300 overflow-hidden ${config.bgColor === color ? 'border-orange-500 scale-110 shadow-[0_0_15px_rgba(234,88,12,0.4)]' : 'border-white/10 hover:scale-110 hover:border-white/30'}`}
                          title={isTransparent ? 'Transparent' : color}
                        />
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-3 block">Accent Color</label>
                  <div className="flex flex-wrap gap-2.5">
                    {accentPresets.map(color => (
                      <button
                        key={color}
                        onClick={() => setConfig({...config, accentColor: color})}
                        style={{ backgroundColor: color }}
                        className={`w-8 h-8 rounded-full border transition-all duration-300 ${config.accentColor === color ? 'border-white scale-110 shadow-[0_0_15px_currentColor]' : 'border-transparent hover:scale-110 hover:border-white/30'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Rating Style" className="z-[50]">
              <CustomSelect 
                value={config.ratingStyle} 
                onChange={(val) => setConfig({...config, ratingStyle: val})} 
                options={ratingStyleOptions} 
              />
            </SectionCard>

            <SectionCard title="Typography" className="z-[40]">
              <CustomSelect 
                value={config.fontFamily} 
                onChange={(val) => setConfig({...config, fontFamily: val})} 
                options={typographyOptions} 
              />
            </SectionCard>

            <SectionCard title="Content Filters" className="z-[30]">
              <div className="space-y-4">
                <div className="relative">
                  <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input 
                    type="text" 
                    placeholder="Keyword match..." 
                    value={config.keyword}
                    onChange={(e) => setConfig({...config, keyword: e.target.value})}
                    className="w-full bg-[#0a0500]/50 border border-orange-500/20 rounded-xl pl-10 pr-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none"
                  />
                </div>
                
                <div className="pt-2 space-y-3">
                  <Checkbox 
                    label="Only show Video reviews" 
                    checked={config.videoOnly} 
                    onChange={(val) => setConfig({...config, videoOnly: val, textOnly: val ? false : config.textOnly})} 
                  />
                  <Checkbox 
                    label="Only show Text reviews" 
                    checked={config.textOnly} 
                    onChange={(val) => setConfig({...config, textOnly: val, videoOnly: val ? false : config.videoOnly})} 
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Advanced Settings" className="z-[20]">
              <div className="space-y-4">
                <Toggle 
                  label="Manual Selection" 
                  enabled={config.manualSelection} 
                  onChange={(val) => setConfig({...config, manualSelection: val})} 
                />
                
                {/* Manual Selection UI */}
                {config.manualSelection && (
                  <div className="mt-4 max-h-48 overflow-y-auto border border-white/10 rounded-lg p-2 bg-black/20 space-y-1 scrollbar-hide">
                    {testimonials.map(t => (
                      <Checkbox 
                        key={t.id}
                        label={`${t.authorName} - ${t.companyName || t.source}`}
                        checked={manualSelectionIds.includes(t.id)}
                        onChange={(val) => {
                          if (val) setManualSelectionIds(prev => [...prev, t.id]);
                          else setManualSelectionIds(prev => prev.filter(id => id !== t.id));
                        }}
                        labelClassName="truncate w-[200px]"
                      />
                    ))}
                    {testimonials.length === 0 && (
                      <div className="text-xs text-zinc-500 text-center py-2">No reviews available</div>
                    )}
                  </div>
                )}

                <Toggle 
                  label="Disable Auto-Resize" 
                  enabled={config.disableAutoResize} 
                  onChange={(val) => setConfig({...config, disableAutoResize: val})} 
                />
              </div>
            </SectionCard>
          </div>

          {/* Right Panel: Live Preview */}
          <div className="lg:col-span-8 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <h3 className="text-sm font-bold tracking-widest text-zinc-500 uppercase">Live Preview</h3>
                <div className="flex items-center gap-1 bg-zinc-900/50 p-1 rounded-lg border border-orange-500/10">
                  <button 
                    onClick={() => setPreviewMode('desktop')} 
                    className={`p-1.5 rounded-md transition-all duration-300 ${previewMode === 'desktop' ? 'bg-orange-500/20 text-orange-400' : 'text-zinc-500 hover:text-zinc-300'}`}
                  >
                    <SafeIcon icon={FiMonitor} />
                  </button>
                  <button 
                    onClick={() => setPreviewMode('mobile')} 
                    className={`p-1.5 rounded-md transition-all duration-300 ${previewMode === 'mobile' ? 'bg-orange-500/20 text-orange-400' : 'text-zinc-500 hover:text-zinc-300'}`}
                  >
                    <SafeIcon icon={FiSmartphone} />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 rounded-2xl border border-orange-500/20 shadow-[0_0_50px_rgba(234,88,12,0.05)] overflow-hidden flex flex-col bg-black relative">
              {/* Fake Browser Toolbar */}
              <div className="h-12 bg-zinc-950 border-b border-orange-500/10 flex items-center px-4 gap-4 z-10 transition-colors">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-zinc-800 border border-zinc-700"></div>
                  <div className="w-3 h-3 rounded-full bg-zinc-800 border border-zinc-700"></div>
                  <div className="w-3 h-3 rounded-full bg-zinc-800 border border-zinc-700"></div>
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 rounded-md bg-[#0a0500] border border-white/5 text-[10px] text-zinc-500 font-mono w-64 text-center truncate">
                    your-website.com
                  </div>
                </div>
              </div>

              {/* Widget Container Area */}
              <div className="flex-1 overflow-y-auto relative flex justify-center">
                <div
                  className={`relative w-full h-full transition-all duration-500 ${previewMode === 'mobile' ? 'max-w-[375px] border-x-4 border-zinc-800 shadow-[0_0_40px_rgba(0,0,0,0.6)] mx-auto' : 'max-w-full'}`}
                  style={{ backgroundColor: config.bgColor, fontFamily: config.fontFamily }}
                >
                  <div className="p-4 sm:p-6 md:p-8 h-full">
                    {displayTestimonials.length > 0 ? (
                      <AnimatePresence mode="wait">
                        {renderLayout()}
                      </AnimatePresence>
                    ) : (
                      <div className="h-64 flex flex-col items-center justify-center text-center">
                        <div className="w-12 h-12 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center mb-3">
                          <SafeIcon icon={FiSearch} className="text-zinc-600" />
                        </div>
                        <p className="text-sm text-zinc-500">No testimonials match your filters.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      )}
    </div>
  );
};

export default WidgetBuilder;